import React from 'react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

import { useScrollLock } from '../../hooks/useScrollLock';
import { useEscapeKey } from '../../hooks/useEscapeKey';

type OverlayBg =
  | 'black'
  | 'white'
  | 'slate'
  | 'zinc'
  | 'neutral'
  | 'gray'
  | 'dark'
  | 'none';

/**
 * 🧩 Tamaños máximos predefinidos para modales
 */
export type ModalMaxWidth =
  | 'max-w-xs'
  | 'max-w-sm'
  | 'max-w-md'
  | 'max-w-lg'
  | 'max-w-xl'
  | 'max-w-2xl'
  | 'max-w-3xl'
  | 'max-w-4xl'
  | 'max-w-5xl'
  | 'max-w-6xl'
  | 'max-w-7xl'
  | 'max-w-full';

interface FormModalProps {
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: ModalMaxWidth;
  overlayBg?: OverlayBg;
  zIndex?: 'normal' | 'front' | 'top';
  /**
   * Nodo opcional que se coloca arriba a la izquierda del modal (espejo del
   * botón cerrar). Pensado para un botón de ayuda que se posiciona a sí mismo.
   */
  headerExtra?: React.ReactNode;
}

const FormModal: React.FC<FormModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-md',
  overlayBg,
  zIndex,
  headerExtra,
}) => {
  useScrollLock(isOpen);

  useEscapeKey(() => {
    if (isOpen && onClose) {
      onClose();
    }
  });

  /**
   * 🎨 Overlay
   */
  const overlayClass = {
    black: 'bg-black/60 backdrop-blur-[2px]',
    white: 'bg-white/70 backdrop-blur-[2px]',
    slate: 'bg-slate-900/60 backdrop-blur-[2px]',
    zinc: 'bg-zinc-900/60 backdrop-blur-[2px]',
    neutral: 'bg-neutral-900/60 backdrop-blur-[2px]',
    gray: 'bg-gray-900/60 backdrop-blur-[2px]',
    dark: 'bg-boxdark/90',
    none: 'bg-transparent',
  }[overlayBg || 'black'];

  /**
   * 🔝 Z-Index
   */
  const zIndexClass =
    zIndex === 'top'
      ? 'z-[99999]'
      : zIndex === 'front'
      ? 'z-[19999]'
      : 'z-[9999]';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={clsx(
            `
              fixed inset-0
              ${zIndexClass}
              flex items-center justify-center
              px-3 py-6
            `,
            overlayClass
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* 🪟 Modal */}
          <motion.div
            className={clsx(
              `
                relative w-full ${maxWidth}

                flex max-h-[92vh] flex-col

                rounded-sm
                border border-stroke
                bg-white
                shadow-default

                dark:border-strokedark
                dark:bg-boxdark
              `
            )}
            initial={{
              scale: 0.96,
              opacity: 0,
              y: 20,
            }}
            animate={{
              scale: 1,
              opacity: 1,
              y: 0,
            }}
            exit={{
              scale: 0.96,
              opacity: 0,
              y: 20,
            }}
            transition={{
              duration: 0.22,
              ease: 'easeOut',
            }}
          >
            {/* ❔ Nodo auxiliar (p. ej. botón de ayuda) */}
            {headerExtra}

            {/* ❌ Botón cerrar */}
            {onClose && (
              <button
                onClick={onClose}
                aria-label="Cerrar modal"
                className="
                  absolute right-3 top-3

                  rounded-sm p-1.5

                  text-body
                  transition

                  hover:bg-gray-2
                  hover:text-black

                  dark:text-bodydark
                  dark:hover:bg-meta-4
                  dark:hover:text-white
                "
              >
                <svg className="h-5 w-5 fill-current" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.8913 9.99599L19.5043 2.38635C20.032 1.85888 20.032 1.02306 19.5043 0.495589C18.9768 -0.0317329 18.141 -0.0317329 17.6135 0.495589L10.0001 8.10559L2.38673 0.495589C1.85917 -0.0317329 1.02343 -0.0317329 0.495873 0.495589C-0.0318274 1.02306 -0.0318274 1.85888 0.495873 2.38635L8.10887 9.99599L0.495873 17.6056C-0.0318274 18.1331 -0.0318274 18.9689 0.495873 19.4964C0.717307 19.7177 1.05898 19.9001 1.4413 19.9001C1.75372 19.9001 2.13282 19.7971 2.40606 19.4771L10.0001 11.8864L17.6135 19.4964C17.8349 19.7177 18.1766 19.9001 18.5589 19.9001C18.8724 19.9001 19.2531 19.7964 19.5265 19.4737C20.0319 18.9452 20.0245 18.1256 19.5043 17.6056L11.8913 9.99599Z"
                  />
                </svg>
              </button>
            )}

            {/* 🧾 Header */}
            {title && (
              <div
                className="
                  border-b border-stroke
                  px-5 py-4
                  dark:border-strokedark
                "
              >
                <h2
                  className="
                    text-center text-base font-semibold
                    text-black dark:text-white
                  "
                >
                  {title}
                </h2>
              </div>
            )}

            {/* 📦 Body */}
            <div
              className="
                scrollbar-thin
                scrollbar-thumb-rounded
                scrollbar-thumb-stroke
                dark:scrollbar-thumb-strokedark

                flex-1 overflow-y-auto

                px-5 py-6

                text-body
                dark:text-bodydark
              "
            >
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FormModal;
